import { z } from "zod";
import { MetricKeyEnum } from "../metrics";

// AI Model Types
export const aiModelTypeSchema = z.enum(["genai-fast", "genai-standard", "vertexai-standard"], {
  errorMap: () => ({ message: "AI model must be one of: genai-fast, genai-standard, vertexai-standard" }),
});

export type AIModelType = z.infer<typeof aiModelTypeSchema>;

// Dataset Schema
export const datasetSchema = z.object({
  name: z.string().min(1, "Dataset name is required"),
  content: z.string().min(1, "Dataset content is required"),
  size: z.number().positive("Dataset size must be positive"),
});

export type Dataset = z.infer<typeof datasetSchema>;

// Dataset Category Response Schema
export const datasetCategoryResponseSchema = z.object({
  success: z.boolean(),
  categories: z.array(z.string()),
  total: z.number(),
});

export type DatasetCategoryResponse = z.infer<typeof datasetCategoryResponseSchema>;

// Datasets by Category Response Schema
export const datasetsByCategoryResponseSchema = z.object({
  success: z.boolean(),
  category: z.string(),
  datasets: z.array(datasetSchema),
  total: z.number(),
});

export type DatasetsByCategoryResponse = z.infer<typeof datasetsByCategoryResponseSchema>;

// AI Models Response Schema
export const aiModelsResponseSchema = z.object({
  success: z.boolean(),
  models: z.object({
    genAI: z.object({
      fast: z.string(),
      standard: z.string(),
    }),
    vertexAI: z.object({
      standard: z.string(),
    }),
  }),
  total: z.number(),
  timestamp: z.string(),
});

export type AIModelsResponse = z.infer<typeof aiModelsResponseSchema>;

// Test Metric Request Schema
export const testMetricRequestSchema = z.object({
  key: z.nativeEnum(MetricKeyEnum, {
    errorMap: () => ({ message: "Invalid metric key" }),
  }),
  aiModel: aiModelTypeSchema,
});

export type TestMetricRequest = z.infer<typeof testMetricRequestSchema>;

// Test Metrics Request Schema
export const testMetricsRequestSchema = z.object({
  resume: z.string().min(1, "Resume content is required"),
  jobDescription: z.string().min(1, "Job description is required"),
  metrics: z.array(testMetricRequestSchema).min(1, "At least one metric must be specified"),
});

export type TestMetricsRequest = z.infer<typeof testMetricsRequestSchema>;

// Test Result Schema
export const testResultSchema = z.object({
  metricKey: z.string(),
  aiModel: z.string(),
  result: z.object({
    key: z.string(),
    metric: z.string(),
    score: z.number(),
    weightedScore: z.number(),
    description: z.string(),
    weight: z.number(),
    object: z.any(),
  }),
  executionTime: z.number(),
});

export type TestResult = z.infer<typeof testResultSchema>;

// Test Metrics Response Schema
export const testMetricsResponseSchema = z.object({
  success: z.boolean(),
  results: z.array(testResultSchema),
  totalExecutionTime: z.number(),
  timestamp: z.string(),
  judgeEvaluation: z.string().optional(),
});

export type TestMetricsResponse = z.infer<typeof testMetricsResponseSchema>;

// Test History Schema for client-side storage
export const testHistorySchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  resumeName: z.string(),
  jobDescriptionName: z.string(),
  results: z.array(testResultSchema),
  totalExecutionTime: z.number(),
  judgeEvaluation: z.string().optional(),
});

export type TestHistory = z.infer<typeof testHistorySchema>;

// Metric Details Schema
export const metricDetailsSchema = z.object({
  key: z.string(),
  metric: z.string(),
  description: z.string(),
  weight: z.number(),
});

export type MetricDetails = z.infer<typeof metricDetailsSchema>;

// Metrics Response Schema
export const metricsResponseSchema = z.object({
  success: z.boolean(),
  metrics: z.array(metricDetailsSchema),
  total: z.number(),
  timestamp: z.string(),
});

export type MetricsResponse = z.infer<typeof metricsResponseSchema>;

// Admin Dashboard Data Schema
export const adminDashboardDataSchema = z.object({
  success: z.boolean(),
  data: z.object({
    users: z.object({
      total: z.number(),
      verified: z.number(),
      anonymous: z.number(),
      plus: z.number(),
      twoFactorEnabled: z.number(),
      recentSignups: z.number(),
    }),
    resumes: z.object({
      total: z.number(),
      public: z.number(),
      private: z.number(),
      locked: z.number(),
      recentlyCreated: z.number(),
    }),
    features: z.object({
      totalAnalyses: z.number(),
      totalOptimizations: z.number(),
      totalUsage: z.number(),
    }),
    system: z.object({
      uptime: z.number(),
      nodeVersion: z.string(),
      databaseStatus: z.string(),
      environment: z.string(),
    }),
  }),
  timestamp: z.string(),
});

export type AdminDashboardData = z.infer<typeof adminDashboardDataSchema>;

// System Health Schema
export const systemHealthSchema = z.object({
  database: z.boolean(),
  uptime: z.number(),
  memory: z.object({
    rss: z.number(),
    heapTotal: z.number(),
    heapUsed: z.number(),
    external: z.number(),
  }),
  version: z.string(),
});

export type SystemHealth = z.infer<typeof systemHealthSchema>;

// Recent Activity Schema
export const recentActivitySchema = z.object({
  newUsers: z.number(),
  newResumes: z.number(),
  recentAnalyses: z.number(),
  recentOptimizations: z.number(),
});

export type RecentActivity = z.infer<typeof recentActivitySchema>;

// Judge AI Analysis Schema
export const judgeAIAnalysisSchema = z.object({
  overallWinner: z.string(),
  reasoning: z.string(),
  metricAnalysis: z.array(z.object({
    metricKey: z.string(),
    metricName: z.string(),
    bestModel: z.string(),
    analysis: z.string(),
    scoreComparison: z.string(),
  })),
  consistencyAnalysis: z.object({
    mostConsistentModel: z.string(),
    explanation: z.string(),
  }),
  qualityAssessment: z.object({
    mostActionableModel: z.string(),
    reasoning: z.string(),
  }),
  improvementSuggestions: z.array(z.object({
    category: z.string(),
    suggestion: z.string(),
    priority: z.enum(['high', 'medium', 'low']),
  })),
  executionTimeAnalysis: z.object({
    fastestModel: z.string(),
    qualityVsSpeedTradeoff: z.string(),
  }),
  nextLevelRecommendations: z.array(z.string()),
});

export type JudgeAIAnalysis = z.infer<typeof judgeAIAnalysisSchema>;

// Judge AI Response Schema
export const judgeAIResponseSchema = z.object({
  success: z.boolean(),
  judgeAnalysis: judgeAIAnalysisSchema,
  timestamp: z.string(),
});

export type JudgeAIResponse = z.infer<typeof judgeAIResponseSchema>;

